import { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { pipe, reject, sort, when } from 'ramda'

import structure from '@/structure'
import { breakpoints, colors, zIndex } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import useRole from '@/hooks/use-role'
import ListingMessageHeader from '@/components/listing-message-header'
import { Label } from '@/components/text'
import useEventListener from '@/components/use-event-listener'

import { Provider } from './context'

const NavBarOutline = styled.div`
  position: fixed;
  left: 0px;
  background-color: white;
  width: 100%;
  z-index: ${zIndex.navBar};

  ${props =>
    props.height &&
    `
  height: ${props.height}px;
  `}

  ${props =>
    props.top &&
    `
  top: ${props.top}px;
  `}

  ${props =>
    props.maxWidth &&
    `
  max-width: ${props.maxWidth}px;
  `}
`
const Navbar = styled.div`
  background-color: white;
  width: 100%;
  z-index: 100;
  border-bottom: 1px solid ${colors.grey88};

  ${props =>
    props.height &&
    `
  height: ${props.height}px;
  `}

  ${props =>
    props.top &&
    `
  top: ${props.top}px;
  `}

  ${props =>
    props.maxWidth &&
    `
  max-width: ${props.maxWidth}px;
  `}
`

const ButtonsPlacementWrapper = styled.div`
  height: 100%;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  height: 100%;

  @media screen and (max-width: ${breakpoints.phoneMax}) {
    gap: 16px;
  }
`

const NavigationButton = styled(Label)`
  color: ${colors.grey56};
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 100%;
  padding-top: 8px;
  padding-bottom: 2px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 12px;
  }

  ${props =>
    props.active &&
    `
    color: ${colors.black};
    border-bottom: 2px solid ${colors.red64};
    padding-bottom: 0px;
  `}
`

const computeActiveSection = (sections, topOffset) => {
  let visible = null
  sections.forEach(section => {
    const sectionRect = section.ref.getBoundingClientRect()
    const sectionDistance = Math.abs(sectionRect.top - topOffset)
    const nearestSectionDistance =
      visible && Math.abs(visible.ref.getBoundingClientRect().top - topOffset)
    const viewingSectionPortion =
      sectionRect.top < topOffset &&
      sectionRect.bottom >
        (window.innerHeight || document.documentElement.clientHeight) * 0.8
    if (
      !visible ||
      sectionDistance <= nearestSectionDistance ||
      viewingSectionPortion
    ) {
      visible = section
    }
  })
  return visible
}

const scrollTo = (node, topOffset) => {
  const scrollOffset = node.getBoundingClientRect().top - topOffset
  'scrollBehavior' in document.documentElement.style
    ? window.scrollBy({
        top: scrollOffset,
        left: 0,
        behavior: 'smooth'
      })
    : window.scrollBy(0, scrollOffset)
}

const insertSection = (sectionId, ref, id) => sections =>
  sort(
    (a, b) =>
      a.ref.getBoundingClientRect().top - b.ref.getBoundingClientRect().top
  )([{ title: sectionId, ref, id }, ...sections])

const InternalNavigation = ({
  navBarHeight = 50,
  children,
  alwaysDisplayNavbar = false,
  ButtonsPlacementComponent = ButtonsPlacementWrapper,
  ButtonsPlacementProps = {},
  listings,
  showTopNav,
  ...rest
}) => {
  const [sections, setSections] = useState([])
  const [activeSection, setActiveSection] = useState()
  const [isNavbarVisible, setIsNavbarVisible] = useState(false)
  const containerRef = useRef()
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const role = useRole()
  const listingState =
    listings.is_hidden === '1' ? 'hidden' : listings.state_machine
  const showMessageHeader = listingState !== 'published' && role === 'landlord'

  const top = displayTablet
    ? structure.header.heightMobile
    : structure.header.height
  const totalHeight = navBarHeight + top

  const registerSection = useCallback(
    (sectionId, ref, previousId, id) =>
      sections.findIndex(item => item.title === sectionId) === -1 &&
      pipe(
        insertSection(sectionId, ref, id),
        when(
          () => !!previousId && previousId !== sectionId,
          reject(section => section.title === previousId)
        ),
        setSections
      )(sections),
    [sections, setSections]
  )

  useEffect(() => {
    setActiveSection(computeActiveSection(sections, totalHeight))
    const isVisible =
      containerRef?.current?.getBoundingClientRect().top <= totalHeight
    setIsNavbarVisible(isVisible)
  }, [sections, totalHeight])

  const onViewChange = useCallback(() => {
    setActiveSection(computeActiveSection(sections, totalHeight))
    const isVisible =
      containerRef?.current?.getBoundingClientRect().top <= totalHeight
    setIsNavbarVisible(isVisible)
  }, [sections, totalHeight])

  useEventListener('scroll', onViewChange)

  return (
    <Provider
      value={{
        registerSection,
        sections
      }}
    >
      {(isNavbarVisible || alwaysDisplayNavbar) && !showTopNav && sections && (
        <NavBarOutline
          top={top}
          height={navBarHeight}
          maxWidth={
            document.documentElement.clientWidth || document.body.clientWidth
          }
        >
          {!displayTablet && (
            <Navbar
              height={navBarHeight}
              maxWidth={
                document.documentElement.clientWidth ||
                document.body.clientWidth
              }
            >
              <ButtonsPlacementComponent {...ButtonsPlacementProps}>
                <ButtonsContainer>
                  {sections.map(({ title, ref, id }) => (
                    <NavigationButton
                      fontSize="14px"
                      onClick={() => scrollTo(ref, totalHeight)}
                      key={title}
                      id={id}
                      active={title === activeSection?.title}
                    >
                      {title}
                    </NavigationButton>
                  ))}
                </ButtonsContainer>
              </ButtonsPlacementComponent>
            </Navbar>
          )}
          {showMessageHeader && <ListingMessageHeader type={listingState} />}
        </NavBarOutline>
      )}
      <div ref={containerRef} {...rest}>
        {children}
      </div>
    </Provider>
  )
}

export default InternalNavigation
